import cn from "classnames";

import { ButtonBuilder } from "@/components/buttonBuilder/buttonBuilder.component";
import { ImageBuilder } from "@/components/imageBuilder/imageBuilder.component";
import { OfferCard } from "@/components/offerCard/offerCard.component";
import { TextBuilder } from "@/components/textBuilder/textBuilder.component";

import type { GetPageQueryResult } from "@/sanity.types";
import { dataAttr } from "@/sanity/utils";

/**
 * Page builder block that arranges its child blocks with flex layout, so standalone text, buttons, images, offer cards, and nested containers can be grouped and positioned together.
 */
export function ContainerBuilder({
  block,
  pageId,
  pageType,
  path,
  isNested = false,
}: ContainerBuilderProps) {
  const children = block?.children;

  if (!children?.length) return null;

  const theme = block.theme;
  const width = block.width ?? "full";
  const hasFullWidthTheme =
    width === "contained" && block.fullWidthTheme === true;
  const responsiveWidth = responsiveWidthStyles(
    width,
    block.tabletWidth,
    block.mobileWidth,
    Boolean(block.border),
  );

  const renderChildren = () => {
    return children.map((child, index) => {
      const Child = ChildBlocks[child._type];
      if (!Child) return null;

      const childPath = `${path}.children[_key=="${child._key}"]`;

      return (
        <div
          key={child._key}
          className={cn(
            child._type === "containerBuilder"
              ? cn(
                  child.width === "contained" && child.fullWidthTheme
                    ? "w-full"
                    : responsiveWidthStyles(
                        child.width ?? "full",
                        child.tabletWidth,
                        child.mobileWidth,
                        Boolean(child.border),
                      ),
                  responsiveJustifySelfStyles(child),
                  responsiveOrderStyles(child),
                  usesFullHeight(child) && "flex flex-col",
                )
              : cn(
                  responsiveHeightStyles(
                    block,
                    (block.direction ?? "column") === "column"
                      ? "grow"
                      : "fill",
                  ),
                  usesFullHeight(block) && "flex flex-col",
                ),
          )}
          data-sanity={dataAttr({
            id: pageId,
            type: pageType,
            path: childPath,
          }).toString()}>
          <Child
            block={child}
            index={index}
            pageId={pageId}
            pageType={pageType}
            path={childPath}
            isNested={child._type === "containerBuilder"}
          />
        </div>
      );
    });
  };
  const content = (
    <div
      data-testid="container-builder"
      className={cn(
        "flex",
        isNested && !hasFullWidthTheme ? "w-full" : responsiveWidth,
        responsiveHeightStyles(block, isNested ? "grow" : "fill"),
        responsiveLayoutStyles(block),
        responsivePaddingStyles(block),
        !isNested && responsiveJustifySelfStyles(block),
        !hasFullWidthTheme && themeStyles(theme),
        {
          "border-greyscale-strength/30 border": block.border,
          "overflow-hidden rounded-lg": block.borderRadius === "small",
          "overflow-hidden rounded-xl": block.borderRadius === "medium",
          "overflow-hidden rounded-2xl": block.borderRadius === "large",
        },
      )}>
      {renderChildren()}
    </div>
  );

  if (!hasFullWidthTheme) return content;

  return (
    <div
      data-testid="container-builder-theme"
      className={cn("w-full", themeStyles(theme))}>
      {content}
    </div>
  );
}

type PageBuilderBlock = NonNullable<
  NonNullable<GetPageQueryResult>["pageBuilder"]
>[number];

export type ContainerBuilderBlock = Extract<
  PageBuilderBlock,
  { _type: "containerBuilder" }
>;

type ResponsiveJustifySelfBlock = Pick<
  ContainerBuilderBlock,
  "justifySelf" | "tabletJustifySelf" | "mobileJustifySelf"
>;

type ContainerBuilderProps = {
  block: ContainerBuilderBlock;
  pageId: string;
  pageType: string;
  /** Path of this container, supplied by BlockRenderer. */
  path: string;
  /** Nested containers are sized by their direct parent flex-item wrapper. */
  isNested?: boolean;
};

type ChildBlocksType = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: React.FC<any>;
};

/**
 * Blocks a container may hold, mirroring the `children` field in the Studio schema. Kept local rather than reusing BlockRenderer's registry, which pulls in every block on the site and would make this a circular import.
 */
const ChildBlocks: ChildBlocksType = {
  textBuilder: TextBuilder,
  buttonBuilder: ButtonBuilder,
  imageBuilder: ImageBuilder,
  offerCard: OfferCard,
  containerBuilder: ContainerBuilder,
};

function widthStyles(
  width: NonNullable<ContainerBuilderBlock["width"]>,
  bordered: boolean,
) {
  return cn({
    "w-full": width === "full" || (width === "contained" && !bordered),
    "w-full md:w-1/2": width === "half",
    "w-full md:w-1/3": width === "oneThird",
    "w-full md:w-2/3": width === "twoThirds",
    "w-full md:w-1/4": width === "quarter",
    "w-[calc(100%_-_2rem)]": width === "contained" && bordered,
    container: width === "contained",
    "mx-auto w-full max-w-3xl": width === "narrow",
  });
}

const widthClasses = {
  base: {
    full: "mx-0 w-full max-w-none",
    half: "mx-0 w-1/2 max-w-none",
    oneThird: "mx-0 w-1/3 max-w-none",
    twoThirds: "mx-0 w-2/3 max-w-none",
    quarter: "mx-0 w-1/4 max-w-none",
    contained: "mx-auto w-full max-w-none",
    narrow: "mx-auto w-full max-w-3xl",
  },
  tablet: {
    full: "md:mx-0 md:w-full md:max-w-none",
    half: "md:mx-0 md:w-1/2 md:max-w-none",
    oneThird: "md:mx-0 md:w-1/3 md:max-w-none",
    twoThirds: "md:mx-0 md:w-2/3 md:max-w-none",
    quarter: "md:mx-0 md:w-1/4 md:max-w-none",
    contained: "md:mx-auto md:w-full md:max-w-[992px]",
    narrow: "md:mx-auto md:w-full md:max-w-3xl",
  },
  desktop: {
    full: "lg:mx-0 lg:w-full lg:max-w-none",
    half: "lg:mx-0 lg:w-1/2 lg:max-w-none",
    oneThird: "lg:mx-0 lg:w-1/3 lg:max-w-none",
    twoThirds: "lg:mx-0 lg:w-2/3 lg:max-w-none",
    quarter: "lg:mx-0 lg:w-1/4 lg:max-w-none",
    contained: "lg:mx-auto lg:w-full lg:max-w-[1440px]",
    narrow: "lg:mx-auto lg:w-full lg:max-w-3xl",
  },
};

type ContainerHeight = "auto" | "full";

type HeightBlock = {
  height?: ContainerHeight | null;
  tabletHeight?: ContainerHeight | null;
  mobileHeight?: ContainerHeight | null;
};

function heightClassMap(includeGrow: boolean) {
  const full = includeGrow ? "h-full grow" : "h-full";
  const auto = includeGrow ? "h-auto grow-0" : "h-auto";
  return {
    base: { auto, full },
    tablet: {
      auto: includeGrow ? "md:h-auto md:grow-0" : "md:h-auto",
      full: includeGrow ? "md:h-full md:grow" : "md:h-full",
    },
    desktop: {
      auto: includeGrow ? "lg:h-auto lg:grow-0" : "lg:h-auto",
      full: includeGrow ? "lg:h-full lg:grow" : "lg:h-full",
    },
  };
}

function usesFullHeight(block: HeightBlock) {
  return (
    block.height === "full" ||
    block.tabletHeight === "full" ||
    block.mobileHeight === "full"
  );
}

function responsiveHeightStyles(block: HeightBlock, kind: "fill" | "grow") {
  const height = block.height ?? "auto";
  if (!block.tabletHeight && !block.mobileHeight) {
    return height === "full"
      ? heightClassMap(kind === "grow").base.full
      : undefined;
  }

  return responsiveValueStyles(
    height,
    block.tabletHeight,
    block.mobileHeight,
    heightClassMap(kind === "grow"),
  );
}

const containedBorderWidthClasses = {
  base: "mx-auto w-[calc(100%_-_2rem)] max-w-none",
  tablet: "md:mx-auto md:w-[calc(100%_-_2rem)] md:max-w-[992px]",
  desktop: "lg:mx-auto lg:w-[calc(100%_-_2rem)] lg:max-w-[1440px]",
};

function widthClassMap(bordered: boolean): ResponsiveClasses {
  if (!bordered) return widthClasses;

  return {
    base: { ...widthClasses.base, contained: containedBorderWidthClasses.base },
    tablet: {
      ...widthClasses.tablet,
      contained: containedBorderWidthClasses.tablet,
    },
    desktop: {
      ...widthClasses.desktop,
      contained: containedBorderWidthClasses.desktop,
    },
  };
}

function responsiveWidthStyles(
  defaultWidth: NonNullable<ContainerBuilderBlock["width"]>,
  tabletWidth: ContainerBuilderBlock["tabletWidth"],
  mobileWidth: ContainerBuilderBlock["mobileWidth"],
  bordered = false,
) {
  if (!tabletWidth && !mobileWidth) return widthStyles(defaultWidth, bordered);

  return responsiveValueStyles(
    defaultWidth,
    tabletWidth,
    mobileWidth,
    widthClassMap(bordered),
  );
}

function themeStyles(theme: ContainerBuilderBlock["theme"]) {
  return cn({
    "bg-primary text-greyscale-body": theme === "primary",
    "bg-secondary text-white": theme === "secondary",
    "bg-primary-100 text-secondary": theme === "tertiary",
    "bg-greyscale-background text-greyscale-body": theme === "light",
    "bg-white text-greyscale-body": theme === "white",
    "bg-success text-white": theme === "success",
    "bg-error text-white": theme === "error",
  });
}

const directionClasses = {
  base: { column: "flex-col", row: "flex-row" },
  tablet: { column: "md:flex-col", row: "md:flex-row" },
  desktop: { column: "lg:flex-col", row: "lg:flex-row" },
};

const justifyClasses = {
  base: {
    start: "justify-start",
    center: "justify-center",
    end: "justify-end",
    between: "justify-between",
  },
  tablet: {
    start: "md:justify-start",
    center: "md:justify-center",
    end: "md:justify-end",
    between: "md:justify-between",
  },
  desktop: {
    start: "lg:justify-start",
    center: "lg:justify-center",
    end: "lg:justify-end",
    between: "lg:justify-between",
  },
};

const alignClasses = {
  base: {
    stretch: "items-stretch",
    start: "items-start",
    center: "items-center",
    end: "items-end",
  },
  tablet: {
    stretch: "md:items-stretch",
    start: "md:items-start",
    center: "md:items-center",
    end: "md:items-end",
  },
  desktop: {
    stretch: "lg:items-stretch",
    start: "lg:items-start",
    center: "lg:items-center",
    end: "lg:items-end",
  },
};

const orderClasses = {
  base: {
    auto: "order-none",
    first: "order-first",
    last: "order-last",
    "1": "order-1",
    "2": "order-2",
    "3": "order-3",
    "4": "order-4",
    "5": "order-5",
    "6": "order-6",
    "7": "order-7",
    "8": "order-8",
    "9": "order-9",
    "10": "order-10",
    "11": "order-11",
    "12": "order-12",
  },
  tablet: {
    auto: "md:order-none",
    first: "md:order-first",
    last: "md:order-last",
    "1": "md:order-1",
    "2": "md:order-2",
    "3": "md:order-3",
    "4": "md:order-4",
    "5": "md:order-5",
    "6": "md:order-6",
    "7": "md:order-7",
    "8": "md:order-8",
    "9": "md:order-9",
    "10": "md:order-10",
    "11": "md:order-11",
    "12": "md:order-12",
  },
  desktop: {
    auto: "lg:order-none",
    first: "lg:order-first",
    last: "lg:order-last",
    "1": "lg:order-1",
    "2": "lg:order-2",
    "3": "lg:order-3",
    "4": "lg:order-4",
    "5": "lg:order-5",
    "6": "lg:order-6",
    "7": "lg:order-7",
    "8": "lg:order-8",
    "9": "lg:order-9",
    "10": "lg:order-10",
    "11": "lg:order-11",
    "12": "lg:order-12",
  },
};

const justifySelfClasses = {
  base: {
    auto: "justify-self-auto",
    stretch: "justify-self-stretch",
    start: "justify-self-start",
    center: "justify-self-center",
    end: "justify-self-end",
  },
  tablet: {
    auto: "md:justify-self-auto",
    stretch: "md:justify-self-stretch",
    start: "md:justify-self-start",
    center: "md:justify-self-center",
    end: "md:justify-self-end",
  },
  desktop: {
    auto: "lg:justify-self-auto",
    stretch: "lg:justify-self-stretch",
    start: "lg:justify-self-start",
    center: "lg:justify-self-center",
    end: "lg:justify-self-end",
  },
};

const gapClasses = {
  base: { none: "gap-0", sm: "gap-2", md: "gap-4", lg: "gap-8" },
  tablet: {
    none: "md:gap-0",
    sm: "md:gap-2",
    md: "md:gap-4",
    lg: "md:gap-8",
  },
  desktop: {
    none: "lg:gap-0",
    sm: "lg:gap-2",
    md: "lg:gap-4",
    lg: "lg:gap-8",
  },
};

const spaceClasses = {
  base: { none: "my-0", sm: "my-4", md: "my-8", lg: "my-14" },
  tablet: {
    none: "md:my-0",
    sm: "md:my-4",
    md: "md:my-8",
    lg: "md:my-14",
  },
  desktop: {
    none: "lg:my-0",
    sm: "lg:my-4",
    md: "lg:my-8",
    lg: "lg:my-14",
  },
};

const paddingClasses = {
  base: { none: "p-0", sm: "p-2", md: "p-4", lg: "p-8" },
  tablet: {
    none: "md:p-0",
    sm: "md:p-2",
    md: "md:p-4",
    lg: "md:p-8",
  },
  desktop: {
    none: "lg:p-0",
    sm: "lg:p-2",
    md: "lg:p-4",
    lg: "lg:p-8",
  },
};

const containedPaddingClasses = {
  base: {
    none: "px-4 py-0",
    sm: "px-4 py-2",
    md: "p-4",
    lg: "p-8",
  },
  tablet: {
    none: "md:px-4 md:py-0",
    sm: "md:px-4 md:py-2",
    md: "md:p-4",
    lg: "md:p-8",
  },
  desktop: {
    none: "lg:px-4 lg:py-0",
    sm: "lg:px-4 lg:py-2",
    md: "lg:p-4",
    lg: "lg:p-8",
  },
};

type ResponsiveClasses = {
  base: Record<string, string>;
  tablet: Record<string, string>;
  desktop: Record<string, string>;
};

function responsiveValueStyles(
  defaultValue: string,
  tabletValue: string | null | undefined,
  mobileValue: string | null | undefined,
  classes: ResponsiveClasses,
) {
  if (!tabletValue && !mobileValue) return classes.base[defaultValue];

  return cn(
    classes.base[mobileValue ?? defaultValue],
    classes.tablet[tabletValue ?? defaultValue],
    classes.desktop[defaultValue],
  );
}

function responsiveLayoutStyles(block: ContainerBuilderBlock) {
  return cn(
    responsiveValueStyles(
      block.direction ?? "column",
      block.tabletDirection,
      block.mobileDirection,
      directionClasses,
    ),
    responsiveValueStyles(
      block.justify ?? "start",
      block.tabletJustify,
      block.mobileJustify,
      justifyClasses,
    ),
    responsiveValueStyles(
      block.align ?? "stretch",
      block.tabletAlign,
      block.mobileAlign,
      alignClasses,
    ),
    responsiveValueStyles(
      block.gap ?? "md",
      block.tabletGap,
      block.mobileGap,
      gapClasses,
    ),
    responsiveValueStyles(
      block.space ?? "md",
      block.tabletSpace,
      block.mobileSpace,
      spaceClasses,
    ),
  );
}

function responsivePaddingStyles(block: ContainerBuilderBlock) {
  const defaultPadding = block.padding ?? "none";
  const defaultWidth = block.width ?? "full";
  const hasOverrides = Boolean(
    block.tabletPadding ||
    block.mobilePadding ||
    block.tabletWidth ||
    block.mobileWidth,
  );

  if (!hasOverrides) {
    const classes =
      defaultWidth === "contained" ? containedPaddingClasses : paddingClasses;
    return classes.base[defaultPadding];
  }

  const mobilePadding = block.mobilePadding ?? defaultPadding;
  const tabletPadding = block.tabletPadding ?? defaultPadding;
  const mobileWidth = block.mobileWidth ?? defaultWidth;
  const tabletWidth = block.tabletWidth ?? defaultWidth;

  return cn(
    (mobileWidth === "contained" ? containedPaddingClasses : paddingClasses)
      .base[mobilePadding],
    (tabletWidth === "contained" ? containedPaddingClasses : paddingClasses)
      .tablet[tabletPadding],
    (defaultWidth === "contained" ? containedPaddingClasses : paddingClasses)
      .desktop[defaultPadding],
  );
}

function responsiveOrderStyles(block: {
  order?: string | null;
  tabletOrder?: string | null;
  mobileOrder?: string | null;
}) {
  const order = block.order || undefined;
  const tabletOrder = block.tabletOrder || undefined;
  const mobileOrder = block.mobileOrder || undefined;
  if (!order && !tabletOrder && !mobileOrder) return undefined;

  return responsiveValueStyles(
    order ?? "auto",
    tabletOrder,
    mobileOrder,
    orderClasses,
  );
}

function responsiveJustifySelfStyles(block: ResponsiveJustifySelfBlock) {
  return responsiveValueStyles(
    block.justifySelf ?? "auto",
    block.tabletJustifySelf,
    block.mobileJustifySelf,
    justifySelfClasses,
  );
}
